/**
 * global-dictionaries/api — panel ITE „Globalne słowniki".
 *
 * Klient API + hooki React Query dla katalogu globalnych słowników zarządzanego
 * przez administratorów ITE (mirror `global_document_types`, ADR-0061).
 * Cykl życia słownika: szkic →
 * opublikowany → wycofany (bez hard-delete po publikacji). Kod jest
 * niezmienialny po publikacji — backend odrzuca zmianę 409 CODE_IMMUTABLE.
 *
 * Endpointy (`/api/admin/global-dictionaries`):
 *   GET    /                          — lista (opcjonalnie ?include_deleted=true)
 *   POST   /                          — utwórz (status='draft')
 *   GET    /:id                       — szczegół + pozycje
 *   PUT    /:id                       — edycja meta (name/description/displayMode/sortOrder; code tylko w draft)
 *   DELETE /:id                       — soft-delete (tylko draft)
 *   POST   /:id/publish               — draft → published
 *   POST   /:id/withdraw              — published → withdrawn
 *   POST   /:id/republish             — withdrawn → published (powrót z wycofania)
 *   POST   /:id/restore               — undelete z kosza (gcrecord → null), status zachowany
 *   POST   /:id/items                 — dodaj pozycję
 *   PUT    /:id/items/:itemId         — edytuj pozycję (label/sortOrder/color/isActive)
 *   DELETE /:id/items/:itemId         — soft-delete pozycji
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

/** Cykl życia słownika globalnego: szkic → opublikowany → wycofany. */
export type GlobalDictionaryStatus = 'draft' | 'published' | 'withdrawn';

/** Sposób wyświetlania pozycji słownika w polach select u tenanta. */
export type GlobalDictionaryDisplayMode = 'label' | 'code' | 'code_label';

/**
 * Rodzaj pozycji słownika globalnego (Etap 10, ADR-0066). 'manual' — pozycje w
 * `global_dictionary_items`, zarządzane w tym panelu. 'external' — pozycje pochodzą z
 * zapytania do zarejestrowanego źródła danych tenanta (`sourceKey` wskazuje wpis rejestru);
 * panel ITE NIE widzi danych tenanta, więc pozycje takiego słownika są tu nieedytowalne
 * (backend blokuje mutacje 409 `DICTIONARY_IS_EXTERNAL`, patrz `apps/api/lib/dictionaries/admin-guards.ts`).
 */
export type GlobalDictionarySourceKind = 'manual' | 'external';

/**
 * Typ zadeklarowanego parametru źródła zewnętrznego (etap 1 modelu dwuetapowego, Faza 2b
 * ADR-0068) — mirror `DictionaryParamType` w `apps/api/lib/dictionaries/external-source-types.ts`.
 * Deklaracja parametru NIE niesie już `kind` (rodzaj wiązania wybiera się dopiero przy
 * podpinaniu pola — etap 2).
 */
export type DictionarySourceParamType = 'text' | 'int' | 'decimal' | 'date' | 'bool';

/** Wszystkie typy parametru — kolejność = kolejność opcji w edytorze listy parametrów SQL. */
export const DICTIONARY_SOURCE_PARAM_TYPES: readonly DictionarySourceParamType[] = [
  'text',
  'int',
  'decimal',
  'date',
  'bool',
] as const;

/**
 * Parametr zadeklarowany przez źródło — kształt zwrócony przez `GET /admin/dictionary-sources`
 * i przechowywany w kolumnie `global_dictionaries.source_params` (źródła SQL, Faza 2b).
 */
export interface DictionarySourceParam {
  name: string;
  type: DictionarySourceParamType;
  required: boolean;
}

/**
 * Metadane jednego zarejestrowanego źródła zewnętrznego (`GET /api/admin/dictionary-sources`,
 * Etap 10.1) — WYŁĄCZNIE metadane rejestru w kodzie backendu, zero danych tenanta. `nameI18nKey`/
 * `descriptionI18nKey` wskazują klucze w namespace `externalSources.<key>.*` (np.
 * `externalSources.active_products.name`) — picker renderuje `t(source.nameI18nKey)`, NIE
 * `source.key` gołym tekstem.
 */
export interface DictionarySourceMeta {
  key: string;
  nameI18nKey: string;
  descriptionI18nKey: string;
  params: DictionarySourceParam[];
}

export interface GlobalDictionaryItem {
  id: string;
  code: string;
  label: string;
  sortOrder: number | null;
  isActive: boolean;
  color: string | null;
}

export interface AdminGlobalDictionary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  displayMode: GlobalDictionaryDisplayMode;
  status: GlobalDictionaryStatus;
  sortOrder: number;
  itemCount?: number;
  gcrecord: string | null;
  /**
   * Etap 10 (ADR-0066) — backend już zawsze serializuje to pole (patrz
   * `apps/api/app/api/admin/global-dictionaries/serialize.ts`), 'manual' dla wszystkich
   * słowników utworzonych przed Etapem 10.
   */
  sourceKind: GlobalDictionarySourceKind;
  /** Klucz rejestru źródeł — non-null WYŁĄCZNIE dla `sourceKind==='external'` z rejestru. */
  sourceKey: string | null;
  /**
   * Zapytanie SQL źródła (Faza 2b/2d, ADR-0068) — non-null WYŁĄCZNIE dla
   * `sourceKind==='external'` w wariancie SQL (XOR z `sourceKey` — dokładnie jedno z dwóch).
   * Widoczne TYLKO w serializerze admina (nigdy w odpowiedziach tenantowych). Opcjonalne w
   * typie dla zgodności tsc z odpowiedziami/mockami sprzed Fazy 2d.
   */
  sourceSql?: string | null;
  /** Parametry zadeklarowane dla źródła SQL (`{name,type,required}[]`) — patrz `sourceSql`. */
  sourceParams?: DictionarySourceParam[] | null;
}

export interface AdminGlobalDictionaryDetail extends AdminGlobalDictionary {
  items: GlobalDictionaryItem[];
}

export interface GlobalDictionaryFormValues {
  code: string;
  name: string;
  description: string;
  displayMode: GlobalDictionaryDisplayMode;
  sortOrder: string;
  /**
   * Etap 10.5 — pole formularza UI. `emptyGlobalDictionaryFormValues()` ustawia domyślnie
   * `sourceKind: 'manual'`, `sourceKey: null`; modal `GlobalDictionaryFormModal` pozwala
   * przełączyć na `external` + wybrać źródło (picker z `useDictionarySources()`), a
   * `buildCreateDictionaryPayload`/`buildUpdateDictionaryPayload` wysyłają te pola.
   * Backend (`CreateGlobalDictionarySchema`/`UpdateGlobalDictionarySchema`) przyjmuje je
   * i waliduje sprzężenie (external ⇒ `sourceKey` z rejestru źródeł; manual ⇒ brak `sourceKey`),
   * lustrząc CHECK `chk_global_dictionaries_source_key_coupling` w bazie. Opcjonalne w typie
   * tylko dla zgodności tsc ze starszymi mockami sprzed Etapu 10.5.
   */
  sourceKind?: GlobalDictionarySourceKind;
  sourceKey?: string | null;
  /**
   * Wariant SQL źródła zewnętrznego (Faza 2d) — treść zapytania SELECT. XOR z `sourceKey`
   * (modal pilnuje: wybór wariantu SQL czyści `sourceKey` i odwrotnie).
   */
  sourceSql?: string | null;
  /** Deklaracje parametrów zapytania SQL (etap 1: nazwa + typ + required). */
  sourceParams?: DictionarySourceParam[];
}

export interface GlobalDictionaryItemFormValues {
  code: string;
  label: string;
  sortOrder: string;
  color: string;
  isActive: boolean;
}

interface ListResponse {
  dictionaries: AdminGlobalDictionary[];
}

interface DetailResponse {
  dictionary: AdminGlobalDictionaryDetail;
}

interface MutateDictionaryResponse {
  dictionary: AdminGlobalDictionary;
}

interface MutateItemResponse {
  item: GlobalDictionaryItem;
}

// ============================================================================
// Query keys
// ============================================================================

export const GLOBAL_DICTIONARIES_LIST_KEY = ['admin', 'global-dictionaries'] as const;
export const globalDictionaryDetailKey = (id: string) =>
  ['admin', 'global-dictionaries', id] as const;

// ============================================================================
// Payload builders
// ============================================================================

/**
 * Etap 10.5: dokłada `sourceKind`/`sourceKey` do payloadu tworzenia. Zawsze wysyła
 * `sourceKind` (domyślnie 'manual' z `emptyGlobalDictionaryFormValues()`); `sourceKey`
 * tylko dla `sourceKind==='external'` — dla 'manual' pole jest pominięte całkowicie (CHECK
 * `chk_global_dictionaries_source_key_coupling` w bazie wymaga `source_key IS NULL` dla
 * słowników ręcznych, patrz ADR-0066 §2). Backend (`CreateGlobalDictionarySchema`,
 * `apps/api/lib/validators/global-dictionaries-admin.ts`) przyjmuje te pola — dostawa
 * równoległa do tego zadania (Etap 10.5b, warstwa admin API).
 */
export function buildCreateDictionaryPayload(
  values: GlobalDictionaryFormValues,
): Record<string, unknown> {
  const sourceKind = values.sourceKind ?? 'manual';
  const body: Record<string, unknown> = {
    code: values.code,
    name: values.name,
    displayMode: values.displayMode,
    sourceKind,
  };
  if (values.description.trim()) body.description = values.description.trim();
  if (values.sortOrder.trim() !== '') body.sortOrder = parseInt(values.sortOrder, 10);
  // XOR źródła zewnętrznego (Faza 2d, ADR-0068): dokładnie jedno z sourceKey (rejestr) /
  // sourceSql (zapytanie SQL). Modal pilnuje rozłączności; builder wysyła tylko aktywny wariant.
  if (sourceKind === 'external') {
    const sql = values.sourceSql?.trim();
    if (sql) {
      body.sourceSql = sql;
      body.sourceParams = values.sourceParams ?? [];
    } else if (values.sourceKey) {
      body.sourceKey = values.sourceKey;
    }
  }
  return body;
}

/**
 * Etap 10.5: `sourceKind`/`sourceKey` dokładane do diffu na tych samych zasadach co
 * pozostałe pola (wysyłane tylko gdy różnią się od `original`). W praktyce diff jest
 * pusty na słownikach zamrożonych przez `codeLocked` w modalu (rodzaj/źródło niezmienne
 * po publikacji, patrz `GlobalDictionaryFormModal`), ale builder nie zakłada blokady UI —
 * liczy się tylko realna różnica wartości, jak przy `code`/`name`/`displayMode`.
 */
export function buildUpdateDictionaryPayload(
  values: GlobalDictionaryFormValues,
  original: AdminGlobalDictionary,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (values.code !== original.code) payload.code = values.code;
  if (values.name !== original.name) payload.name = values.name;
  const desc = values.description.trim() || null;
  if (desc !== original.description) payload.description = desc;
  if (values.displayMode !== original.displayMode) payload.displayMode = values.displayMode;
  const sort = values.sortOrder.trim() !== '' ? parseInt(values.sortOrder, 10) : null;
  if (sort !== original.sortOrder) payload.sortOrder = sort;
  const sourceKind = values.sourceKind ?? 'manual';
  if (sourceKind !== original.sourceKind) payload.sourceKind = sourceKind;
  // XOR (Faza 2d): wariant SQL wyklucza sourceKey i odwrotnie — liczymy wartości EFEKTYWNE
  // dla obu pól i wysyłamy tylko realne różnice względem `original` (jak code/name wyżej).
  const sourceSql = sourceKind === 'external' ? values.sourceSql?.trim() || null : null;
  const sourceKey = sourceKind === 'external' && !sourceSql ? (values.sourceKey ?? null) : null;
  if (sourceKey !== original.sourceKey) payload.sourceKey = sourceKey;
  if (sourceSql !== (original.sourceSql ?? null)) payload.sourceSql = sourceSql;
  const sourceParams = sourceSql ? (values.sourceParams ?? []) : null;
  const originalParams = original.sourceParams ?? null;
  if (JSON.stringify(sourceParams) !== JSON.stringify(originalParams)) {
    payload.sourceParams = sourceParams;
  }
  return payload;
}

export function buildCreateItemPayload(
  values: GlobalDictionaryItemFormValues,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    code: values.code,
    label: values.label,
  };
  if (values.sortOrder.trim() !== '') body.sortOrder = parseInt(values.sortOrder, 10);
  if (values.color.trim()) body.color = values.color.trim();
  return body;
}

export function buildUpdateItemPayload(
  values: GlobalDictionaryItemFormValues,
  original: GlobalDictionaryItem,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (values.label !== original.label) payload.label = values.label;
  const sort = values.sortOrder.trim() !== '' ? parseInt(values.sortOrder, 10) : null;
  if (sort !== original.sortOrder) payload.sortOrder = sort;
  const color = values.color.trim() || null;
  if (color !== original.color) payload.color = color;
  if (values.isActive !== original.isActive) payload.isActive = values.isActive;
  return payload;
}

// ============================================================================
// Hooks — dictionary sources (Etap 10.5 — picker źródeł zewnętrznych w formularzu)
// ============================================================================

interface DictionarySourcesResponse {
  sources: DictionarySourceMeta[];
}

export const DICTIONARY_SOURCES_KEY = ['admin', 'dictionary-sources'] as const;

/**
 * Metadane zarejestrowanych źródeł zewnętrznych (`GET /api/admin/dictionary-sources`,
 * Etap 10.1) — zasila picker w `GlobalDictionaryFormModal` (follow-up agent) gdy admin
 * wybiera `sourceKind='external'`. Rejestr jest kodem backendu (nie danymi z bazy), więc
 * `staleTime` długi — lista zmienia się tylko przy deployu nowej wersji API.
 */
export function useDictionarySources() {
  return useQuery<DictionarySourceMeta[]>({
    queryKey: DICTIONARY_SOURCES_KEY,
    queryFn: async () => {
      const res = await api.get<DictionarySourcesResponse>('/admin/dictionary-sources');
      return res.data.sources;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// ============================================================================
// Hooks — SQL source test (Faza 2d, ADR-0068) + tenant picker dla przycisku „Testuj"
// ============================================================================

/**
 * Body `POST /api/admin/global-dictionaries/test-sql` — endpoint testowy BEZ `[id]` (działa
 * także podczas tworzenia słownika, przed pierwszym zapisem). ITE wybiera tenanta, na którego
 * bazie (rolą read-only per tenant, ADR-0068) zostanie wykonane zapytanie.
 */
export interface TestGlobalDictionarySqlInput {
  sourceSql: string;
  sourceParams: DictionarySourceParam[];
  tenantId: string;
  companyId?: string;
  /** Przykładowe wartości parametrów (nazwa → wartość) do podstawienia w teście. */
  paramValues?: Record<string, unknown>;
}

/** Wynik testu SQL — grid pierwszych wierszy + licznik + flaga obcięcia (ROW_CAP). */
export interface TestGlobalDictionarySqlResult {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  truncated: boolean;
}

/**
 * Mutacja „Testuj zapytanie SQL" — reużywa wykonawcy `sql-source-executor.ts` po stronie
 * backendu. Błędy walidacji wracają jako 400 z kodami `SQL_NOT_SELECT` /
 * `SQL_MULTIPLE_STATEMENTS` / `SQL_PARAM_UNDECLARED` (mapowane na komunikaty w modalu).
 */
export function useTestGlobalDictionarySql() {
  return useMutation({
    mutationFn: async (input: TestGlobalDictionarySqlInput) => {
      const res = await api.post<TestGlobalDictionarySqlResult>(
        '/admin/global-dictionaries/test-sql',
        input,
      );
      return res.data;
    },
  });
}

/** Minimalny wiersz tenanta dla pickera „Testuj na tenancie” (podzbiór `TenantListItem`). */
export interface AdminTenantLite {
  id: string;
  name: string;
  slug: string;
  code: string;
}

export const ADMIN_TENANTS_LITE_KEY = ['admin', 'tenants', 'lite-picker'] as const;

/**
 * Lekka lista tenantów do pickera testu SQL — reużywa `GET /api/admin/tenants` (ten sam
 * endpoint co strona AdminTenants), z podniesionym limitem (backend max 100). `enabled`
 * pozwala modalowi nie płacić za fetch, dopóki admin nie wejdzie w wariant SQL.
 */
export function useAdminTenantsLite(enabled: boolean) {
  return useQuery<AdminTenantLite[]>({
    queryKey: ADMIN_TENANTS_LITE_KEY,
    queryFn: async () => {
      const res = await api.get<{
        tenants: Array<{ id: string; name: string; slug: string; code: string }>;
      }>('/admin/tenants?limit=100');
      return res.data.tenants.map((t) => ({ id: t.id, name: t.name, slug: t.slug, code: t.code }));
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// ============================================================================
// Hooks — dictionaries
// ============================================================================

export function useGlobalDictionaries(includeDeleted: boolean) {
  return useQuery<AdminGlobalDictionary[]>({
    queryKey: [...GLOBAL_DICTIONARIES_LIST_KEY, { includeDeleted }] as const,
    queryFn: async () => {
      const url = includeDeleted
        ? '/admin/global-dictionaries?include_deleted=true'
        : '/admin/global-dictionaries';
      const res = await api.get<ListResponse>(url);
      return res.data.dictionaries;
    },
    retry: false,
  });
}

export function useGlobalDictionaryDetail(id: string | null) {
  return useQuery<AdminGlobalDictionaryDetail>({
    queryKey: id ? globalDictionaryDetailKey(id) : ['admin', 'global-dictionaries', 'none'],
    queryFn: async () => {
      const res = await api.get<DetailResponse>(`/admin/global-dictionaries/${id}`);
      return res.data.dictionary;
    },
    enabled: !!id,
    retry: false,
  });
}

function useInvalidateGlobalDictionaries() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: GLOBAL_DICTIONARIES_LIST_KEY });
    if (id) queryClient.invalidateQueries({ queryKey: globalDictionaryDetailKey(id) });
  };
}

export function useCreateGlobalDictionary() {
  const invalidate = useInvalidateGlobalDictionaries();
  return useMutation({
    mutationFn: async (values: GlobalDictionaryFormValues) => {
      const res = await api.post<MutateDictionaryResponse>(
        '/admin/global-dictionaries',
        buildCreateDictionaryPayload(values),
      );
      return res.data.dictionary;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateGlobalDictionary() {
  const invalidate = useInvalidateGlobalDictionaries();
  return useMutation({
    mutationFn: async ({
      id,
      values,
      original,
    }: {
      id: string;
      values: GlobalDictionaryFormValues;
      original: AdminGlobalDictionary;
    }) => {
      const res = await api.put<MutateDictionaryResponse>(
        `/admin/global-dictionaries/${id}`,
        buildUpdateDictionaryPayload(values, original),
      );
      return res.data.dictionary;
    },
    onSuccess: (dict) => invalidate(dict.id),
  });
}

export function usePublishGlobalDictionary() {
  const invalidate = useInvalidateGlobalDictionaries();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<MutateDictionaryResponse>(
        `/admin/global-dictionaries/${id}/publish`,
      );
      return res.data.dictionary;
    },
    onSuccess: (dict) => invalidate(dict.id),
  });
}

export function useWithdrawGlobalDictionary() {
  const invalidate = useInvalidateGlobalDictionaries();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<MutateDictionaryResponse>(
        `/admin/global-dictionaries/${id}/withdraw`,
      );
      return res.data.dictionary;
    },
    onSuccess: (dict) => invalidate(dict.id),
  });
}

export function useRepublishGlobalDictionary() {
  const invalidate = useInvalidateGlobalDictionaries();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<MutateDictionaryResponse>(
        `/admin/global-dictionaries/${id}/republish`,
      );
      return res.data.dictionary;
    },
    onSuccess: (dict) => invalidate(dict.id),
  });
}

export function useRestoreGlobalDictionary() {
  const invalidate = useInvalidateGlobalDictionaries();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<MutateDictionaryResponse>(
        `/admin/global-dictionaries/${id}/restore`,
      );
      return res.data.dictionary;
    },
    onSuccess: (dict) => invalidate(dict.id),
  });
}

export function useDeleteGlobalDictionary() {
  const invalidate = useInvalidateGlobalDictionaries();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/global-dictionaries/${id}`);
      return id;
    },
    onSuccess: (id) => invalidate(id),
  });
}

// ============================================================================
// Hooks — items
// ============================================================================

export function useAddGlobalDictionaryItem(dictionaryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: GlobalDictionaryItemFormValues) => {
      const res = await api.post<MutateItemResponse>(
        `/admin/global-dictionaries/${dictionaryId}/items`,
        buildCreateItemPayload(values),
      );
      return res.data.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: globalDictionaryDetailKey(dictionaryId) });
    },
  });
}

export function useUpdateGlobalDictionaryItem(dictionaryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      values,
      original,
    }: {
      itemId: string;
      values: GlobalDictionaryItemFormValues;
      original: GlobalDictionaryItem;
    }) => {
      const res = await api.put<MutateItemResponse>(
        `/admin/global-dictionaries/${dictionaryId}/items/${itemId}`,
        buildUpdateItemPayload(values, original),
      );
      return res.data.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: globalDictionaryDetailKey(dictionaryId) });
    },
  });
}

export function useDeleteGlobalDictionaryItem(dictionaryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      await api.delete(`/admin/global-dictionaries/${dictionaryId}/items/${itemId}`);
      return itemId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: globalDictionaryDetailKey(dictionaryId) });
    },
  });
}
